// Card for the outside REST API: issue, disable and delete bearer keys, and a
// link to the Swagger page. Lives on the Public API page (`public-api`
// section, granted by the `api` addon). Keys are personal: everyone sees and
// manages the keys they issued, a super admin sees everyone's with the owner
// named.
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { Button } from './Button';
import {
  createPublicApiKey, deletePublicApiKey, listPublicApiKeys, updatePublicApiKey, type PublicApiKey,
} from '@/api/public-api-keys';
import { formatDateTime } from '@/lib/datetime';

export const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
export const DOCS_URL = `${API_BASE}/public/docs`;

export function PublicApiKeys() {
  const admin = useAuthStore((s) => s.admin);
  const showOwner = admin?.role === 'super_admin';
  const canEdit = !admin?.read_only;
  const qc = useQueryClient();
  const { data: keys = [], isLoading } = useQuery({ queryKey: ['public-api-keys'], queryFn: listPublicApiKeys });
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['public-api-keys'] });
  const create = useMutation({
    mutationFn: (n: string) => createPublicApiKey(n),
    onSuccess: (row) => {
      setIssued({ name: row.name, key: row.key });
      setName('');
      setCopied(false);
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (k: PublicApiKey) => updatePublicApiKey(k.id, { enabled: !k.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deletePublicApiKey(id),
    onSuccess: invalidate,
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate(name.trim());
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-content">Keys</h2>
        <p className="text-sm text-content-subtle mt-1">
          Bearer keys for the outside REST API: released apps with texts, icon, screenshots and the
          weekly "advertised" stamp, and PWA provisioning per project, GEO and platform. A key is yours:
          what it creates is attributed to you. Docs and a try-it-out console:{' '}
          <a href={DOCS_URL} target="_blank" rel="noreferrer" className="text-primary hover:underline">{DOCS_URL}</a>
        </p>
      </div>

      <div className="px-6 py-5 space-y-5">
        {canEdit && (
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Key name, e.g. poster bot"
            className="block w-full sm:max-w-sm px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          />
          <Button type="submit" loading={create.isPending} disabled={!name.trim()}>Issue key</Button>
        </form>
        )}
        {create.isError && (
          <p className="text-xs text-danger">{(create.error as Error).message}</p>
        )}

        {issued && (
          <div className="border border-warning/40 bg-warning/10 rounded-md p-4 space-y-2">
            <p className="text-sm text-content">
              Key for <span className="font-medium">{issued.name}</span>. Copy it now: it is shown only once.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <code className="block flex-1 px-3 py-2 bg-surface border border-border rounded font-mono text-xs break-all select-all">{issued.key}</code>
              <Button size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
              <Button size="sm" variant="secondary" onClick={() => setIssued(null)}>Done</Button>
            </div>
            <pre className="text-xs text-content-subtle overflow-x-auto">{[
              `curl -H "Authorization: Bearer ${issued.key}" "${window.location.origin}${API_BASE}/public/apps?per_page=5"`,
              `curl -X POST -H "Authorization: Bearer ${issued.key}" -H "Content-Type: application/json" \\`,
              `  -d '{"project":"Taskly","geo":"GB","platform":"android"}' "${window.location.origin}${API_BASE}/public/pwa"`,
            ].join('\n')}</pre>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-content-subtle">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-content-subtle">No keys yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-subtle border-b border-border">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  {showOwner && <th className="py-2 pr-4 font-medium">Owner</th>}
                  <th className="py-2 pr-4 font-medium">Key</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 pr-4 font-medium">Last used</th>
                  <th className="py-2 pr-4 font-medium">Calls</th>
                  <th className="py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-border last:border-0">
                    <td className="py-2 pr-4 text-content">{k.name}</td>
                    {showOwner && (
                      <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{k.owner_name || k.owner_email || '—'}</td>
                    )}
                    <td className="py-2 pr-4 font-mono text-xs text-content-muted">{k.key_prefix}…</td>
                    <td className="py-2 pr-4">
                      <span className={k.enabled ? 'text-success' : 'text-content-subtle'}>{k.enabled ? 'enabled' : 'disabled'}</span>
                    </td>
                    <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{formatDateTime(k.created_at)}</td>
                    <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{formatDateTime(k.last_used_at, 'never')}</td>
                    <td className="py-2 pr-4 text-content-muted">{k.use_count}</td>
                    <td className="py-2 whitespace-nowrap">
                      {canEdit && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="secondary" onClick={() => toggle.mutate(k)} loading={toggle.isPending && toggle.variables?.id === k.id}>
                          {k.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => { if (window.confirm(`Delete key "${k.name}"? Scripts using it stop working.`)) remove.mutate(k.id); }}
                          loading={remove.isPending && remove.variables === k.id}
                        >
                          Delete
                        </Button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
